import { wallEdit } from './editing.js';
import { assertWallLengthLockTransition } from './wallLengthLocks.js';

const finite = (value, min, max) => Number.isFinite(value) && value >= min && value <= max;
const id = value => typeof value === 'string' && value.trim() === value && value.length > 0 && [...value].length <= 80;
const point = (x,y) => finite(x,-100000,100000) && finite(y,-100000,100000);
function list(plan,key,limit) {
  const values=plan[key];
  if (!Array.isArray(values) || values.length>limit) throw new Error(`This floor has an invalid or oversized ${key} list.`);
  const seen=new Set();
  for (const value of values) {
    if (!value || !id(value.id) || seen.has(value.id)) throw new Error(`The ${key} need valid unique IDs before this wall can be edited.`);
    seen.add(value.id);
  }
}

/** Review a rigid wall edit in plan XY: 0° points right, 90° points down in 2D.
 * No measurement is created, no adjoining wall or independent area is moved,
 * and the supplied document is never mutated (including rejected edits). */
export function reviewWallPose(plan,wallID,{x,y,direction}={}) {
  if (!plan || plan._siteLevels || plan._groundSelections || plan._sceneWalls) throw new Error('Choose a wall on its own editable floor before moving it.');
  if (!id(wallID) || !point(x,y) || !finite(direction,-360000,360000)) throw new Error('Enter a valid wall, start-point coordinates within ±100000 m, and a direction within ±360000°.');
  list(plan,'walls',4000); list(plan,'cams',500); list(plan,'openings',400);
  const original=plan.walls.find(w=>w.id===wallID);
  if (!original) throw new Error('The selected wall no longer exists on this floor.');
  if (!point(original.x1,original.y1) || !point(original.x2,original.y2) || !finite(original.h,.1,100000)
    || original.wallPts || original.roofPts || original.objectKind) throw new Error('This wall needs geometry review before its position can be edited.');
  const metres=Math.hypot(original.x2-original.x1,original.y2-original.y1);
  if (!finite(metres,.1,300000)) throw new Error('Choose a wall with a valid length of at least 0.1 m.');
  for (const opening of plan.openings.filter(o=>o.wallId===wallID)) {
    if (!['door','window','passage'].includes(opening.kind) || !finite(opening.along,0,metres) || !finite(opening.width,.01,metres)
      || !finite(opening.height,.01,100000) || opening.bottom !== undefined && !finite(opening.bottom,0,100000)) throw new Error('An opening on this wall needs geometry review before moving it.');
  }
  const angle=((direction%360)+360)%360*Math.PI/180;
  // Exact cardinal components avoid introducing tiny offsets at site bounds.
  const cosine=Math.abs(Math.cos(angle))<1e-15 ? 0 : Math.cos(angle), sine=Math.abs(Math.sin(angle))<1e-15 ? 0 : Math.sin(angle);
  const patch={x1:x,y1:y,x2:x+metres*cosine,y2:y+metres*sine};
  if (!point(patch.x2,patch.y2)) throw new Error('This direction would move the far end of the wall beyond supported Site coordinates.');
  const doc=structuredClone(plan), wall=doc.walls.find(w=>w.id===wallID), edit=wallEdit(doc,wall,patch);
  if (edit.error) throw new Error(edit.error);
  if (edit.cams.some(c=>!point(c.x,c.y) || !finite(c.h,0,100000) || !Number.isFinite(c.pan))) throw new Error('This move would place a mounted camera outside supported Site coordinates.');
  Object.assign(wall,edit.object);
  for (const camera of edit.cams) Object.assign(doc.cams.find(c=>c.id===camera.id),camera);
  // This is a rigid move, so retain the exact original host distance and all
  // aperture details instead of adding floating-point length-ratio drift.
  for (const opening of edit.openings) Object.assign(doc.openings.find(o=>o.id===opening.id),opening,{along:plan.openings.find(o=>o.id===opening.id).along});
  assertWallLengthLockTransition(plan,doc);
  doc.review=null;
  return {
    doc,before:structuredClone(original),after:structuredClone(wall),
    affected:{cameras:edit.cams.length,openings:edit.openings.length},
    warnings:[
      'Connected walls and independent room outlines stay in their existing positions. Review gaps, overlaps and camera coverage before applying this wall move.',
      'Original measurement and capture evidence is retained. Review measurement checks after moving the wall; this edit is not a new measurement.'
    ]
  };
}
